import { useState, useEffect, useRef } from 'react';
import { uploadReferenceImage, getImageUrl } from '../../services/firebase';
import { Button } from './Button';
import toast from 'react-hot-toast';

export interface ReferenceImageUploadProps {
  brandId: string;
  /** Needed for the storage path watchBrands/{brandId}/references/{refId}/images/ */
  referenceId: string;
  /** Current main image — a storage path OR an http URL. */
  imageUrl?: string;
  /** Optional source-URL field. */
  sourceUrl?: string;
  onImageUrlChange: (url: string) => void;
  onSourceUrlChange: (url: string) => void;
  disabled?: boolean;
}

/**
 * Reusable main-image upload control for a reference, mirroring the main-image
 * section of the super-admin ReferenceEditor. Owns its own preview + uploading
 * + file-input state; the parent owns the image URL and source URL.
 *
 * Single image only — the gallery is a separate concept and deliberately not
 * part of this component.
 */
export function ReferenceImageUpload({
  brandId,
  referenceId,
  imageUrl = '',
  sourceUrl = '',
  onImageUrlChange,
  onSourceUrlChange,
  disabled = false,
}: ReferenceImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // The last imageUrl we resolved/set ourselves. Lets the sync effect tell an
  // external change (a reference being loaded for editing) apart from our edits.
  const lastSyncedUrl = useRef<string | undefined>(undefined);

  // Sync the preview when imageUrl changes from outside this component.
  useEffect(() => {
    if (imageUrl === lastSyncedUrl.current) return;
    lastSyncedUrl.current = imageUrl;
    if (!imageUrl) {
      setPreview(null);
      return;
    }
    if (imageUrl.startsWith('http')) {
      setPreview(imageUrl);
      return;
    }
    // Storage path — resolve to a download URL.
    setPreview(null);
    getImageUrl(imageUrl).then((url) => setPreview(url)).catch(() => {});
  }, [imageUrl]);

  const handlePaste = (value: string) => {
    lastSyncedUrl.current = value;
    setPreview(value.startsWith('http') ? value : null);
    onImageUrlChange(value);
  };

  const handleRemove = () => {
    lastSyncedUrl.current = '';
    setPreview(null);
    setError(null);
    onImageUrlChange('');
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !brandId || !referenceId) return;
    setUploading(true);
    setError(null);
    try {
      const storagePath = await uploadReferenceImage(brandId, referenceId, file);
      lastSyncedUrl.current = storagePath;
      onImageUrlChange(storagePath);
      const url = await getImageUrl(storagePath);
      setPreview(url);
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <label className='block text-sm font-medium text-gray-700 mb-1'>
        Image
      </label>

      {/* Preview */}
      <div className='aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3 relative'>
        {preview ? (
          <img
            src={preview}
            alt='Reference image'
            className='w-full h-full object-cover'
          />
        ) : (
          <div className='w-full h-full flex items-center justify-center text-gray-400'>
            <svg
              className='w-16 h-16'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          </div>
        )}
      </div>

      {/* Upload / replace / remove */}
      <input
        type='file'
        ref={fileRef}
        onChange={handleFile}
        accept='image/*'
        className='hidden'
      />
      <div className='flex gap-2'>
        <Button
          variant='secondary'
          className='flex-1'
          onClick={() => fileRef.current?.click()}
          loading={uploading}
          disabled={disabled}
        >
          {preview ? 'Replace Image' : 'Upload Image'}
        </Button>
        {preview && (
          <Button
            variant='danger'
            size='sm'
            onClick={handleRemove}
            disabled={disabled || uploading}
            title='Remove image'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
              />
            </svg>
          </Button>
        )}
      </div>

      {/* Paste-URL input */}
      <div className='mt-2 flex items-center gap-2'>
        <span className='text-xs text-gray-400'>or</span>
        <input
          type='text'
          value={imageUrl}
          onChange={(e) => handlePaste(e.target.value)}
          placeholder='Paste URL...'
          disabled={disabled}
          className='flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
        />
      </div>

      {error && <p className='mt-2 text-xs text-red-600'>{error}</p>}

      {/* Source URL */}
      <div className='mt-3'>
        <label className='block text-xs font-medium text-gray-600 mb-1'>
          Source URL
        </label>
        <input
          type='url'
          value={sourceUrl}
          onChange={(e) => onSourceUrlChange(e.target.value)}
          placeholder='https://...'
          disabled={disabled}
          className='w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
        />
      </div>
    </div>
  );
}
